import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { motion, useAnimation } from 'framer-motion';
import { debounce } from 'lodash';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { useEffect, useMemo, useState } from 'react';
import React from 'react';
import styled from 'styled-components';

import { CrossSvg, SearchSvg, StarFilledSvg } from '../../../assets/icons';
import CommonCoreSvg from '../../../assets/icons/CommonCoreSvg';
import ELASvg from '../../../assets/icons/ELASvg';
import FoldedHandsSVG from '../../../assets/icons/FoldedHands';
import MathSvg from '../../../assets/icons/MathSvg';
import ScienceSvg from '../../../assets/icons/ScienceSvg';
import SocialStudiesSvg from '../../../assets/icons/SocialStudiesSvg';
import theme from '../../../assets/theme';
import { Button } from '../../../components/_atoms/Button';
import { IconWrapper } from '../../../components/_atoms/IconWrapper';
import { GeneratorCard } from '../../../components/_molecules/GeneratorCard';
import { NewLandingPageFooter } from '../../../components/_molecules/LandingPageFooter';
import { ResponsiveGeneratorsGridContainer } from '../../../components/AuthenticatedHomeV2';
import NewAppLayout from '../../../components/layouts/NewAppLayout';
import { useGenerateStandardFromSearch } from '../../../hooks/useGenerateStandardFromSearch';
import { useGetGeneratorsById } from '../../../hooks/useGetGeneratorsById';
import { useMetrics } from '../../../hooks/useMetrics';
import usePaywallPrompt from '../../../hooks/usePaywallPrompt';
import { useQueryGeneratorsByStandard } from '../../../hooks/useQueryGeneratorsByStandard';
import useRoutePrefetch from '../../../hooks/useRoutePrefetch';
import useStandards from '../../../hooks/useStandards';
import { Standards } from '../../../services/backendService/types';
import { getGeneratorsUrlFromStandards } from '../../../services/DiscoverService/discoverUtils';
import { getConfig } from '../../../utils/store';
import { NewMainContainerWithGeneratorForm } from '../../generators/[seoTitle]/g/[generatorId]';

export type LearningStandard = {
  label: string;
  code: string;
  subjects: Subject[];
  icon: JSX.Element;
  color: string;
};

export type Subject = {
  label: string;
  icon: JSX.Element;
  color: string;
  id: string;
  placeholder: string;
};

export type GeneratedStandard = {
  id: string;
  'standard-code'?: string;
  'standard-description'?: string;
  'standard-relevancy'?: string;
};

export const supportedStandards: LearningStandard[] = [
  {
    label: 'Common Core State Standards',
    code: 'CCSS',
    subjects: [
      {
        label: 'English Language Arts / Literacy',
        icon: <ELASvg />,
        color: theme.colors.blue,
        id: 'ELA',
        placeholder: 'CCSS.ELA-LITERACY.W.6.1',
      },
      {
        label: 'Mathematics',
        icon: <MathSvg />,
        color: theme.colors.orange,
        id: 'math',
        placeholder: 'CCSS.MATH.CONTENT.6.NS.B.4',
      },
    ],
    icon: <CommonCoreSvg />,
    color: theme.colors.blue,
  },
  {
    label: 'Texas Essential Knowledge and Skills',
    code: 'TEKS',

    subjects: [
      {
        label: 'English Language Arts and Reading',
        icon: <ELASvg />,
        color: theme.colors.blue,
        id: 'ELA',
        placeholder: 'TEKS-ELAR-6.1',
      },
      {
        label: 'Social Studies',
        icon: <SocialStudiesSvg />,
        color: theme.colors.purple,
        id: 'socialStudies',
        placeholder: 'The American Revolution',
      },
      {
        label: 'Science',
        icon: <ScienceSvg />,
        color: theme.colors.green,
        id: 'science',
        placeholder: 'TEKS-SCIENCE-6.1',
      },
      {
        label: 'Mathematics',
        icon: <MathSvg />,
        color: theme.colors.orange,
        id: 'math',
        placeholder: 'TEKS-MATH-6.1',
      },
    ],
    icon: <StarFilledSvg />,
    color: theme.colors.purple,
  },
  {
    label: 'Next Generation Science Standards',
    code: 'NGSS',
    subjects: [
      {
        label: 'Science',
        icon: <ScienceSvg />,
        color: theme.colors.green,
        id: 'science',
        placeholder: 'NGSS-MS-PS1-3',
      },
    ],
    icon: <ScienceSvg />,
    color: theme.colors.green,
  },
  {
    label: 'National Curriculum Standards for the Social Studies',
    code: 'NCSS',
    subjects: [
      {
        label: 'Social Studies',
        icon: <SocialStudiesSvg />,
        color: theme.colors.purple,
        id: 'socialStudies',
        placeholder: 'The American Revolution',
      },
    ],
    icon: <SocialStudiesSvg />,
    color: theme.colors.orange,
  },
];

const BrowseByStandardPage = () => {
  const session = useSession();
  // Prefetch a single generator page
  useRoutePrefetch(`/generators/[generatorName]/g/[generatorId]`);

  return (
    <>
      <NewAppLayout allowUnAuthenticated={false}>
        <PageContainer $isAuthenticated={session.status === 'authenticated'}>
          <BrowseByStandard />
          <NewLandingPageFooter footerBackgroundColor={theme.colors.paleBeige} />
        </PageContainer>
      </NewAppLayout>
    </>
  );
};

const TopPannel = ({
  standardSelected,
  subjectSelected,
  gradeSelected,
  strandSelected,
  searchedItemSelected,
  setStandardSelected,
  setSubjectSelected,
  setGradeSelected,
  setStrandSelected,
  setSearchValue,
  setSearchedItemSelected,
}: {
  standardSelected: LearningStandard | undefined;
  subjectSelected: Subject | undefined;
  gradeSelected: string | undefined;
  strandSelected: Standards | undefined | undefined;
  searchedItemSelected: GeneratedStandard | undefined;
  setStandardSelected: (standard: LearningStandard | undefined) => void;
  setSubjectSelected: (subject: Subject | undefined) => void;
  setGradeSelected: (grade: string | undefined) => void;
  setStrandSelected: (strand: Standards | undefined) => void;
  setSearchValue: (val?: string) => void;
  setSearchedItemSelected: (val: GeneratedStandard | undefined) => void;
}) => {
  const handleClickStandard = () => {
    setStandardSelected(undefined);
    setSubjectSelected(undefined);
    setGradeSelected(undefined);
    setStrandSelected(undefined);
    setSearchValue(undefined);
    setSearchedItemSelected(undefined);
  };

  const handleClickSubject = () => {
    setSubjectSelected(undefined);
    setGradeSelected(undefined);
    setStrandSelected(undefined);
    setSearchValue(undefined);
    setSearchedItemSelected(undefined);
  };

  const handleClickGrade = () => {
    setGradeSelected(undefined);
    setStrandSelected(undefined);
    setSearchValue(undefined);
    setSearchedItemSelected(undefined);
  };

  const handleClickStrand = () => {
    setStrandSelected(undefined);
    setSearchValue(undefined);
    setSearchedItemSelected(undefined);
  };

  return (
    <TopbarContainer>
      {!!standardSelected && (
        <FancyButton
          isTopBar={true}
          onClick={handleClickStandard}
          color={standardSelected.color}
          icon={standardSelected.icon}
        >
          {standardSelected.label}
        </FancyButton>
      )}
      {!!subjectSelected && (
        <FancyButton
          isTopBar={true}
          onClick={handleClickSubject}
          color={subjectSelected.color}
          icon={subjectSelected.icon}
        >
          {subjectSelected.label}
        </FancyButton>
      )}
      {!!gradeSelected && (
        <FancyButton
          isTopBar={true}
          onClick={handleClickGrade}
          color={theme.colors.green}
          icon={null}
        >
          {gradeSelected} grade
        </FancyButton>
      )}
      {!!strandSelected && (
        <FancyButton
          isTopBar={true}
          onClick={handleClickStrand}
          color={theme.colors.green}
          icon={null}
        >
          {strandSelected.label}
        </FancyButton>
      )}
      {!!searchedItemSelected && (
        <FancyButton
          isTopBar={true}
          onClick={handleClickStrand}
          color={theme.colors.green}
          icon={null}
        >
          {searchedItemSelected['standard-code']}
        </FancyButton>
      )}
    </TopbarContainer>
  );
};

const TopbarContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
  padding: 0px 20px;
  margin-bottom: 10px;
`;

const FancyButton = ({
  children,
  onClick,
  color,
  icon,
  isTopBar,
}: {
  children: React.ReactNode;
  onClick: () => void;
  color: string;
  icon: React.ReactNode;
  isTopBar?: boolean;
}) => {
  const animation = useAnimation();
  const hoverSequence = async () => {
    await animation.start({ scale: 1.03, transition: { duration: 0.1 } });
    animation.start({ scale: 1, transition: { duration: 0.1 } });
  };

  return (
    <motion.div animate={animation} onHoverStart={hoverSequence} onClick={onClick}>
      <FancyIconButton
        $color={color}
        icon={
          icon ? (
            <ButtonIconWrapper $isTopbar={isTopBar} $color={color}>
              {icon}
            </ButtonIconWrapper>
          ) : (
            undefined
          )
        }
        $isTopbar={isTopBar}
      >
        {children}
      </FancyIconButton>
    </motion.div>
  );
};

const StandardSelectionSection = ({
  setStandardSelected,
  setSubjectSelected,
}: {
  setStandardSelected: (standard: LearningStandard) => void;
  setSubjectSelected: (subject: Subject) => void;
}) => {
  const handleSetStandardSelected = (standard: LearningStandard) => {
    setStandardSelected(standard);
    if (standard.code === 'NGSS') {
      //NGSS is only science so we automatically set the subject
      setSubjectSelected({
        label: 'Science',
        icon: <ScienceSvg />,
        color: theme.colors.green,
        id: 'science',
        placeholder: 'NGSS-MS-PS1-3',
      });
    }

    if (standard.code === 'NCSS') {
      //NCSS is only social studies so we automatically set the subject
      setSubjectSelected({
        label: 'Social Studies',
        icon: <SocialStudiesSvg />,
        color: theme.colors.purple,
        id: 'socialStudies',
        placeholder: 'The American Revolution',
      });
    }
  };

  return (
    <StandardSelectionSectionContainer>
      {supportedStandards.map(standard => (
        <FancyButton
          key={standard.code}
          onClick={() => handleSetStandardSelected(standard)}
          color={standard.color}
          icon={
            <ButtonIconWrapper $color={standard.color}>{standard.icon}</ButtonIconWrapper>
          }
        >
          {standard.label}
        </FancyButton>
      ))}
    </StandardSelectionSectionContainer>
  );
};

const FancyIconButton = styled(Button)<{ $color: string; $isTopbar?: boolean }>`
  background-color: ${theme.colors.white};
  color: ${theme.colors.black};
  font-size: 20px;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: start;
  gap: 10px;
  height: 100px;
  width: 300px;
  border-bottom: 5px solid ${({ $color }) => $color};

  span {
    white-space: wrap;
    text-align: left;
  }

  svg {
    width: 30px;
    height: 30px;
    margin: 0px;
    padding: 2px;
  }

  &:hover,
  &:focus,
  &:active {
    background: ${theme.colors.white};
    color: ${theme.colors.black};
  }

  ${({ $isTopbar, $color }) =>
    $isTopbar &&
    `
    border-bottom: 2px solid ${$color};
    height: 40px;
    padding: 10px;
    width: fit-content;
    svg {
      width: 20px;
      height: 20px;
    }


    span {
    font-size: 16px;
    font-weight: 400;
    white-space: nowrap;
    }
  `}
`;

const ButtonIconWrapper = styled(IconWrapper)<{ $color: string; $isTopbar?: boolean }>`
  min-width: 40px;
  min-height: 40px;
  height: 40px;
  width: 40px;
  background-color: ${({ $color }) => $color};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  color: ${theme.colors.white};

  ${({ $isTopbar }) =>
    $isTopbar &&
    `
    min-width: 30px;
    min-height: 30px;
    height: 30px;
    width: 30px;
    `}
`;

const StandardSelectionSectionContainer = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
  margin: 0 20px;
`;

const SubjectSelectionSection = ({
  standardSelected,
  setSubjectSelected,
}: {
  standardSelected: LearningStandard;
  setSubjectSelected: (subject: Subject) => void;
}) => {
  return (
    <StandardSelectionSectionContainer>
      {standardSelected.subjects.map(subject => (
        <FancyButton
          key={subject.label}
          onClick={() => setSubjectSelected(subject)}
          color={subject.color}
          icon={
            <ButtonIconWrapper $color={subject.color}>{subject.icon}</ButtonIconWrapper>
          }
        >
          {subject.label}
        </FancyButton>
      ))}
    </StandardSelectionSectionContainer>
  );
};

const GradeSelectionSection = ({
  setGradeSelected,
}: {
  setGradeSelected: (grade: string) => void;
}) => {
  return (
    <StandardSelectionSectionContainer style={{ maxWidth: 800, margin: '0 auto' }}>
      {['3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th', '11th', '12th'].map(
        grade => (
          <GradeSelectorButton key={grade} onClick={() => setGradeSelected(grade)}>
            {grade} grade
          </GradeSelectorButton>
        ),
      )}
    </StandardSelectionSectionContainer>
  );
};

const SearchInputComp = styled.input`
  border-radius: 5px;
  background: ${theme.colors.white};
  outline: none;
  border: none;
  height: 100%;
  width: 100%;
  padding-left: 40px;
  font-weight: bold;
  color: ${theme.colors.black};
`;

const SearchInput = ({
  value,
  onChange,
  handleGenerateStandards,
  isGenerateStandardsEnabled,
  placeholder,
}: {
  value?: string;
  onChange: (val?: string) => void;
  handleGenerateStandards: (searchedValue: string) => void;
  isGenerateStandardsEnabled: boolean;
  placeholder: string;
}) => {
  const handleSearchValueChanged = (value?: string) => {
    if (!value || value.length === 0) {
      onChange(undefined);
      return;
    }
    onChange(value);
  };

  const handleSearchValueChangedDebounced = useMemo(
    () => debounce(handleGenerateStandards, 1000),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (value && isGenerateStandardsEnabled) handleSearchValueChangedDebounced(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <SearchInputContainer>
      <IconWrapper
        style={{
          position: 'absolute',
          left: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
        }}
        height="20px"
        width="20px"
        color="#5B5B5A"
      >
        <SearchSvg />
      </IconWrapper>
      <SearchInputComp
        placeholder={placeholder}
        value={value || ''}
        onChange={e => handleSearchValueChanged(e.target.value)}
      />
      {value && value.length > 0 && (
        <IconWrapper
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
          onClick={() => handleSearchValueChanged('')}
          height="15px"
          width="15px"
          color="#5B5B5A"
        >
          <CrossSvg />
        </IconWrapper>
      )}
    </SearchInputContainer>
  );
};

const SearchInputContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  height: 52px;

  @media (max-width: 800px) {
    height: 32px;
  }
`;

export const orderCCSSELAStandards = (standards: Standards[]) => {
  //CCSS standards are in the format CCSS.ELA-LITERACY.RL.6.1 or CCSS.ELA-LITERACY.RL.6.1a

  const splitStandardsCodes = standards.map(standard => {
    //first remove the first part of the standard code CCSS-ELA-LITERACY- or CCSS.ELA-LITERACY.
    //leaving just the strand, grade and strand code
    const trimmedStandardCode = standard.label
      .replace('CCSS.ELA-LITERACY-', '')
      .replace('CCSS-ELA-LITERACY-', '')
      .replace('CCSS.ELA-LITERACY.', '');

    const [strand, grade, strandCode] = trimmedStandardCode.split('.');

    const strandCodeNumber = strandCode.match(/\d+/);

    //remove any numbers from the strandCode
    const strandCodeLetter = strandCode.match(/[a-zA-Z]+/);

    return {
      standard: standard,
      grade: parseInt(grade),
      strand: strand,
      strandCode: strandCode,
      strandCodeNumber: strandCodeNumber ? parseInt(strandCodeNumber[0]) : undefined,
      strandCodeLetter: strandCodeLetter ? strandCodeLetter[0] : undefined,
    };
  });

  const orderedStrands = splitStandardsCodes.slice().sort((a, b) => {
    // Strand order as it appears on the CC website
    const strandOrder = ['RL', 'RI', 'RF', 'W', 'L'];

    const gradeComparison = a.grade - b.grade;
    if (gradeComparison !== 0) return gradeComparison;

    const strandComparison =
      strandOrder.indexOf(a.strand) - strandOrder.indexOf(b.strand);
    if (strandComparison !== 0) return strandComparison;

    if (a.strandCodeNumber !== undefined && b.strandCodeNumber !== undefined) {
      const numberComparison = a.strandCodeNumber - b.strandCodeNumber;
      if (numberComparison !== 0) return numberComparison;
    }

    if (a.strandCodeLetter && b.strandCodeLetter) {
      return a.strandCodeLetter.localeCompare(b.strandCodeLetter);
    }

    if (a.strandCodeLetter) return 1;
    if (b.strandCodeLetter) return -1;

    return 0;
  });

  return orderedStrands.map(standard => standard.standard);
};

export const orderTEKSELAStandards = (standards: Standards[]) => {
  //TEKS standards are in the format TEKS-ELAR-6.1 or TEKS-ELAR-6.1A
  //first split the standard code by - or .
  const splitStandardsCodes = standards.map(standard => {
    const [standardCode, subject, gradeAndCode] = standard.label.split('-' || '.');
    //remove any letters from the strandCode
    const [grade, strandCode] = gradeAndCode.split('.');
    const strandCodeNumber = strandCode.match(/\d+/);

    //remove any numbers from the strandCode
    const strandCodeLetter = strandCode.match(/[a-zA-Z]+/);
    return {
      standard: standard,
      grade: grade,
      strandCode: strandCode,
      strandCodeNumber: strandCodeNumber ? parseInt(strandCodeNumber[0]) : undefined,
      strandCodeLetter: strandCodeLetter ? strandCodeLetter[0] : undefined,
    };
  });

  const orderedStrands = splitStandardsCodes.slice().sort((a, b) => {
    //first compare by grade (numerical order) 3, 4, 5, 6, 7, 8, E1, E2, E3, E4
    const gradeOrder = ['3', '4', '5', '6', '7', '8', 'E1', 'E2', 'E3', 'E4'];
    const gradeComparison = gradeOrder.indexOf(a.grade) - gradeOrder.indexOf(b.grade);
    if (gradeComparison !== 0) return gradeComparison;

    if (a.strandCodeNumber !== undefined && b.strandCodeNumber !== undefined) {
      const numberComparison = a.strandCodeNumber - b.strandCodeNumber;
      if (numberComparison !== 0) return numberComparison;
    }

    if (a.strandCodeLetter && b.strandCodeLetter) {
      return a.strandCodeLetter.localeCompare(b.strandCodeLetter);
    }

    if (a.strandCodeLetter) return 1;
    if (b.strandCodeLetter) return -1;

    return 0;
  });

  return orderedStrands.map(standard => standard.standard);
};

const StrandSelectionSection = ({
  setStrandSelected,
  strands,
  isLoading,
  searchValue,
  standardSelected,
  subjectSelected,
  gradeSelected,
  setSearchValue,
  setSearchedItemSelected,
}: {
  setStrandSelected: (strand: Standards | undefined) => void;
  strands: Standards[] | undefined;
  isLoading: boolean;
  searchValue: string | undefined;
  standardSelected: LearningStandard | undefined;
  subjectSelected: Subject;
  gradeSelected: string | undefined;
  setSearchValue: (val?: string) => void;
  setSearchedItemSelected: (val?: GeneratedStandard) => void;
}) => {
  const metrics = useMetrics();
  const {
    generate,
    data: generatedStandards,
    isLoading: isGeneratingStandards,
  } = useGenerateStandardFromSearch();

  //Filter the strands based on the standard, subject and grade selected
  const memoizedFilteredStrands: Standards[] = useMemo(() => {
    if (!standardSelected || !subjectSelected || !gradeSelected) return [];

    // Regex patterns for grade matching
    const regex = /[\.\-][A-Za-z]*[\.\-](\d+)(?:-\d+)?/; // CCSS (all grades) and TEKS (grades 3-8)
    const highschoolTeksRegex = /-E([1-4])/; // TEKS (grades 9-12 - E1-E4)

    // Mapping for high school grades
    const highschoolGradeMapping: { [key: string]: string } = {
      '1': '9th',
      '2': '10th',
      '3': '11th',
      '4': '12th',
    };

    return (
      strands?.filter(strand => {
        const strandGrade = strand.label.match(regex)?.[0];
        const highschoolStrandGrade = strand.label.match(highschoolTeksRegex)?.[1];
        const highschoolGradeToNumber = highschoolStrandGrade
          ? highschoolGradeMapping[highschoolStrandGrade]
          : undefined;

        const gradeNumber = gradeSelected.replace(/\D/g, '');
        const isStandardMatch = strand.label.includes(standardSelected.code);
        const isSubjectMatch = strand.label.includes(subjectSelected.id);
        const isGradeMatch =
          (strandGrade && strandGrade.includes(gradeNumber)) ||
          highschoolGradeToNumber === gradeSelected;

        return isStandardMatch && isSubjectMatch && isGradeMatch;
      }) || []
    );
  }, [strands, standardSelected, subjectSelected, gradeSelected]);

  const searchedStrands = (standardSelected?.code === 'CCSS'
    ? orderCCSSELAStandards(memoizedFilteredStrands)
    : standardSelected?.code === 'TEKS'
    ? orderTEKSELAStandards(memoizedFilteredStrands)
    : memoizedFilteredStrands
  ).filter(strand => {
    if (!searchValue) return true;
    const combinedText = `${strand.label} ${strand.description}`.toLowerCase();

    const searchTerms = searchValue
      .split(' ')
      .map(term => term.trim())
      .filter(term => term);

    const matchesAllTerms = (text: string) => {
      return searchTerms.every(term => {
        // The regex takes each substring of the search value and escapes special characters (like . * + ? etc.)
        // to ensure they are treated as literal characters. It then creates a case-insensitive pattern to match
        // the substring anywhere within the either the label or description of the standard.
        const regex = new RegExp(term.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'i');
        return regex.test(text);
      });
    };
    return matchesAllTerms(combinedText);
  });

  const handleGenerateStandards = (searchedValue: string) => {
    metrics.logEvent('BrowseStandardStandard.SearchAndGenerate', {
      searchValue: searchedValue,
      standardSelected,
      subjectSelected,
      gradeSelected,
    });
    generate({
      prompt: {
        searchValue: searchedValue,
        standardSelected: standardSelected?.label,
        subjectSelected: subjectSelected?.label,
        gradeSelected,
      },
    });
  };

  //reduce the generated standards to an array
  const generatedStandardsArray = Object.keys(generatedStandards || {})?.reduce(
    (acc, key) => {
      if (!generatedStandards) return acc;
      // Extract the index from the key
      const indexMatch = key.match(/standards-output\.(\d+)\./);
      if (!indexMatch) return acc;

      const index = parseInt(indexMatch[1], 10);
      const fieldName = key.split('.').pop();

      if (!acc[index]) {
        acc[index] = { id: generatedStandards[key].id }; // Initialize the object with the id
      }

      if (fieldName === 'standard-code') {
        acc[index]['standard-code'] = generatedStandards[key].value;
      } else if (fieldName === 'standard-description') {
        acc[index]['standard-description'] = generatedStandards[key].value;
      } else if (fieldName === 'standard-relevancy') {
        acc[index]['standard-relevancy'] = generatedStandards[key].value;
      }

      return acc;
    },
    [] as GeneratedStandard[],
  );

  const handleGeneratedStandardSelected = (standard: GeneratedStandard | undefined) => {
    if (!standard) return;
    setSearchedItemSelected(standard);
    setSearchValue(
      `${standard['standard-code']} ${standard['standard-description']} ${standard[
        'standard-relevancy'
      ] || ''}`,
    );
    setStrandSelected(undefined);
    metrics.logEvent('BrowseStandardStandard.GeneratedStandardSelected', {
      standardCode: standard['standard-code'],
      standardDescription: standard['standard-description'],
      standardSelected,
      subjectSelected,
      gradeSelected,
    });
  };

  const handleELAStrandSelected = (strand: Standards) => {
    setStrandSelected(strand);
    metrics.logEvent('BrowseStandardStandard.StrandSelected.ELA', {
      strandLabel: strand.label,
      strandDescription: strand.description,
      standardSelected,
      subjectSelected,
      gradeSelected,
      searchValue, //If the user searched for a standard we will see what they searched
    });
  };

  const highlightText = (text: string, searchTerms: string[]) => {
    if (!searchTerms.length) return text;

    // Escape special characters for regex
    const regex = new RegExp(
      searchTerms.map(term => term.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')).join('|'),
      'gi',
    );

    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, index) => {
          const match = text.match(regex)?.[index];
          return (
            <React.Fragment key={index}>
              {part}
              {match && (
                <span
                  style={{
                    backgroundColor: theme.colors.veryLightGreen,
                    fontWeight: 'bold',
                  }}
                >
                  {match}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  const searchTerms =
    searchValue
      ?.split(' ')
      .map((term: string) => term.trim())
      .filter((term: string) => term) || [];

  const isELA = subjectSelected?.id === 'ELA';
  const noStrandsFound = !!searchedStrands && searchedStrands.length === 0;
  const hasGeneratedStandards =
    !!generatedStandardsArray && generatedStandardsArray.length > 0;
  const isSearching = isLoading || isGeneratingStandards;
  const shouldShowNoStandardsMessage =
    strands && !isSearching && noStrandsFound && !hasGeneratedStandards;

  return (
    <StandardSelectionSectionContainer
      style={{ width: '100%', maxWidth: 800, margin: '0 auto', padding: '0px 20px' }}
    >
      <SearchInput
        value={searchValue}
        onChange={setSearchValue}
        isGenerateStandardsEnabled={subjectSelected.id !== 'ELA'}
        handleGenerateStandards={handleGenerateStandards}
        placeholder={subjectSelected.placeholder}
      />
      {!!strands && (
        <StrandsContainer>
          {/* ELA Specific Strands */}
          {isELA &&
            !!searchedStrands &&
            searchedStrands.map(strand => (
              <Strand key={strand.label} onClick={() => handleELAStrandSelected(strand)}>
                <Label>{highlightText(strand.label, searchTerms)}</Label>
                <Description>
                  {highlightText(strand.description, searchTerms)}
                </Description>
              </Strand>
            ))}
          {/* Non-ELA Strands - generated standards */}
          {generatedStandardsArray && (
            <>
              {generatedStandardsArray.map(standard => (
                <Strand
                  key={standard.id}
                  onClick={() => handleGeneratedStandardSelected(standard)}
                >
                  <Label>{standard['standard-code']}</Label>{' '}
                  <Description>
                    {standard['standard-description']} {standard['standard-relevancy']}
                  </Description>
                </Strand>
              ))}
            </>
          )}
          {isSearching && (
            <ResponsiveGeneratorsGridContainer
              style={{
                width: '100%',
                maxHeight: 180,
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 100, color: theme.colors.orange }}
                    spin
                  />
                }
              />
            </ResponsiveGeneratorsGridContainer>
          )}
          {/* No Strands or Standards Found Message */}
          {shouldShowNoStandardsMessage && (
            <Strand
              key={searchValue}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {isELA ? (
                <>
                  <IconWrapper height={'100px'} width={'100px'}>
                    <FoldedHandsSVG />
                  </IconWrapper>
                  <Label style={{ fontSize: 20 }}>
                    No standards found, please try again
                  </Label>
                </>
              ) : (
                <Label>Search your standard to get started</Label>
              )}
            </Strand>
          )}
        </StrandsContainer>
      )}
    </StandardSelectionSectionContainer>
  );
};

const StrandsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
  padding: 10px;
  border-radius: 10px;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  width: 100%;
  max-width: 800px;
`;

const Strand = styled.div`
  padding: 10px;
  border-radius: 5px 5px 0px 0px;
  color: ${theme.colors.black};
  font-weight: bold;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 100%;
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${theme.colors.greyLight};

  &:hover {
    background-color: ${theme.colors.veryLightGreen};
    border-radius: 5px;
    border-bottom: 0px solid ${theme.colors.iconGrey};
  }
`;

const Label = styled.span`
  font-size: 18px;
`;

const Description = styled.span`
  font-size: 18px;
  font-weight: 400;
`;

const GradeSelectorButton = styled(Button)`
  background-color: ${theme.colors.white};
  color: ${theme.colors.black};
  font-size: 20px;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 30px;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.orange};
    color: ${theme.colors.white};
  }
`;

const GeneratorSearchEmptyState = ({
  searchedStandard,
  subjectSelected,
  grade,
}: {
  searchedStandard: string | undefined;
  subjectSelected: string;
  grade: string;
}) => {
  const config = getConfig();

  const fullLessonIdToFetch =
    subjectSelected === 'ELA'
      ? config.discovery.fullLessonIds
      : subjectSelected === 'socialStudies'
      ? config.discovery.fullLessonIdsSocialStudies
      : subjectSelected === 'science'
      ? config.discovery.fullLessonIdsScience
      : config.discovery.fullLessonIdsMath;

  const { generators, isFetchingGenerators } = useGetGeneratorsById(fullLessonIdToFetch);

  const searchPhrase = searchedStandard;

  return (
    <div>
      {isFetchingGenerators && !generators && (
        <ResponsiveGeneratorsGridContainer
          style={{
            width: '100%',
            maxHeight: 180,
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Spin
            indicator={
              <LoadingOutlined
                style={{ fontSize: 100, color: theme.colors.orange }}
                spin
              />
            }
          />
        </ResponsiveGeneratorsGridContainer>
      )}
      {generators?.[0] && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            padding: '20px',
          }}
        >
          <NewMainContainerWithGeneratorForm
            authenticated={true}
            generator={generators?.[0]}
            searchPhrase={searchPhrase}
            grade={grade}
            isFullLessonEmptyState={true}
          />
        </div>
      )}
    </div>
  );
};

const GeneratorSearchGrid = ({
  selectedStandard,
  gradeSelected,
  subjectSelected,
}: {
  selectedStandard: Standards;
  gradeSelected: string;
  subjectSelected: Subject;
}) => {
  const { userAccessBadge } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });

  const {
    generators: generatorsFromStandard,
    isFetchingGenerators,
  } = useQueryGeneratorsByStandard({
    standardId: selectedStandard.id,
  });

  return (
    <>
      {isFetchingGenerators && generatorsFromStandard.length === 0 && (
        <ResponsiveGeneratorsGridContainer
          style={{
            width: '100%',
            maxHeight: 180,
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Spin
            indicator={
              <LoadingOutlined
                style={{ fontSize: 100, color: theme.colors.orange }}
                spin
              />
            }
          />
        </ResponsiveGeneratorsGridContainer>
      )}
      {generatorsFromStandard.length === 0 && !isFetchingGenerators && (
        <GeneratorSearchEmptyState
          searchedStandard={`${selectedStandard.label} ${selectedStandard.description}`}
          grade={gradeSelected}
          subjectSelected={subjectSelected.id}
        />
      )}
      <ResponsiveGeneratorsGridContainer>
        {generatorsFromStandard.length > 0 &&
          generatorsFromStandard.map(generator => {
            return (
              <Link
                href={getGeneratorsUrlFromStandards({
                  title: generator.title,
                  generatorId: generator.id,
                  standardId: selectedStandard.id,
                  grade: gradeSelected,
                  subject: subjectSelected.id,
                })}
                key={generator.id}
              >
                <a style={{ height: '100%', width: '100%' }}>
                  <GeneratorCard
                    subTitle=""
                    title={generator.title || ''}
                    coverImageId={generator.coverImageId}
                    status={generator.status}
                    firstPublished={generator.firstPublished}
                    isPremiumGenerator={generator?.aiContent?.isPremiumGenerator}
                    generatorPlan={generator?.aiContent?.generatorPlan}
                    description={generator.description || ''}
                    userAccessBadge={userAccessBadge}
                    learningStandards={generator.learningStandards}
                  />
                </a>
              </Link>
            );
          })}
      </ResponsiveGeneratorsGridContainer>
    </>
  );
};

const BrowseByStandard = () => {
  const [standardSelected, setStandardSelected] = useState<LearningStandard>();
  const [subjectSelected, setSubjectSelected] = useState<Subject>();
  const [gradeSelected, setGradeSelected] = useState<string>();
  const [strandSelected, setStrandSelected] = useState<Standards | undefined>();
  const [searchValue, setSearchValue] = useState<string | undefined>();
  const [searchedItemSelected, setSearchedItemSelected] = useState<
    GeneratedStandard | undefined
  >();

  const { standards: strands, isFetchingStandards } = useStandards({ isGenerator: true });
  return (
    <BrowseByStandardContainer>
      <TopSection>
        <Title style={{ color: theme.colors.white, margin: 0 }} $fontSize={36}>
          Browse Lessons by Standard
        </Title>
      </TopSection>
      <TopPannel
        standardSelected={standardSelected}
        subjectSelected={subjectSelected}
        gradeSelected={gradeSelected}
        strandSelected={strandSelected}
        searchedItemSelected={searchedItemSelected}
        setStandardSelected={setStandardSelected}
        setSubjectSelected={setSubjectSelected}
        setGradeSelected={setGradeSelected}
        setStrandSelected={setStrandSelected}
        setSearchValue={setSearchValue}
        setSearchedItemSelected={setSearchedItemSelected}
      />
      {!standardSelected && (
        <>
          <Title $fontSize={50}>I am using...</Title>
          <StandardSelectionSection
            setStandardSelected={setStandardSelected}
            setSubjectSelected={setSubjectSelected}
          />
        </>
      )}

      {standardSelected && !subjectSelected && (
        <>
          <Title $fontSize={50}>I am teaching...</Title>
          <SubjectSelectionSection
            standardSelected={standardSelected}
            setSubjectSelected={setSubjectSelected}
          />
        </>
      )}

      {standardSelected && subjectSelected && !gradeSelected && (
        <>
          <Title $fontSize={50}>My students are...</Title>
          <GradeSelectionSection setGradeSelected={setGradeSelected} />
        </>
      )}
      {standardSelected &&
        subjectSelected &&
        gradeSelected &&
        !strandSelected &&
        !searchedItemSelected && (
          <>
            <Title $fontSize={50}>My strand is...</Title>
            <StrandSelectionSection
              strands={strands}
              isLoading={isFetchingStandards}
              setStrandSelected={setStrandSelected}
              setSearchValue={setSearchValue}
              searchValue={searchValue}
              standardSelected={standardSelected}
              subjectSelected={subjectSelected}
              gradeSelected={gradeSelected}
              setSearchedItemSelected={setSearchedItemSelected}
            />
          </>
        )}
      {standardSelected &&
        subjectSelected &&
        gradeSelected &&
        strandSelected &&
        !searchedItemSelected && (
          <>
            <Title style={{ marginBottom: 5 }} $fontSize={45}>
              Lessons Aligned with:
            </Title>
            <p
              style={{
                fontSize: 20,
                fontWeight: 400,
                textAlign: 'center',
                maxWidth: 800,
                margin: '0 auto',
              }}
            >
              <span
                style={{
                  marginRight: 10,
                }}
              >
                {' '}
                {strandSelected.label}
              </span>
            </p>
            <GeneratorSearchGrid
              selectedStandard={strandSelected}
              gradeSelected={gradeSelected}
              subjectSelected={subjectSelected}
            />
          </>
        )}
      {standardSelected &&
        subjectSelected &&
        gradeSelected &&
        !strandSelected &&
        searchedItemSelected && (
          <>
            <Title style={{ marginBottom: 5 }} $fontSize={45}>
              Lessons Aligned with:
            </Title>
            <p
              style={{
                fontSize: 20,
                fontWeight: 400,
                textAlign: 'center',
                maxWidth: 800,
                margin: '0 auto',
              }}
            >
              <span
                style={{
                  marginRight: 10,
                }}
              >
                {' '}
                {searchedItemSelected['standard-code']
                  ? searchedItemSelected['standard-code']
                  : ''}
              </span>
            </p>
            <GeneratorSearchEmptyState
              searchedStandard={searchValue}
              grade={gradeSelected}
              subjectSelected={subjectSelected.id}
            />
          </>
        )}
    </BrowseByStandardContainer>
  );
};

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.lightGreen};
  border-radius: 10px;
  align-items: center;
  padding: 20px;
  margin: 20px 20px 10px 20px;
  background: radial-gradient(
      circle at 50px 50px,
      ${theme.colors.translucentWhite} 90px,
      transparent 91px
    ),
    radial-gradient(
      circle at calc(100% - 150px) 150px,
      ${theme.colors.translucentWhite} 120px,
      transparent 91px
    ),
    radial-gradient(
      circle at 200px calc(100% - 110px),
      ${theme.colors.translucentWhite} 50px,
      transparent 51px
    ),
    ${theme.colors.lightGreen};
  color: ${theme.colors.white};

  p {
    font-size: 18px;
    font-weight: 600;
    text-align: center;

    @media (max-width: 800px) {
      font-size: 16px;
    }
  }

  @media (max-width: 800px) {
    border-radius: 0px;
    justify-content: end;
    background: radial-gradient(
        circle at 50px -20px,
        ${theme.colors.translucentWhite} 90px,
        transparent 91px
      ),
      ${theme.colors.lightGreen};
    box-shadow: 0px 4px 4px rgb(0, 0, 0, 0.1);
    margin: 0px 0px 20px 0px;
  }
`;

const PageContainer = styled.div<{ $isAuthenticated: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  background: ${theme.colors.background};

  @media (min-width: 801px) {
    padding-left: ${({ $isAuthenticated }) => ($isAuthenticated ? '240px' : '0px')};
  }
`;

const BrowseByStandardContainer = styled.div`
  min-height: calc(100vh - 60px);
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 60px 0px 20px 0px;
  position: relative;

  @media (max-width: 800px) {
    padding: 60px 0px 20px 0px;
  }
`;

const Title = styled.h3<{ $fontSize: number }>`
  justify-content: center;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  color: ${theme.colors.black};
  display: flex;
  align-items: center;
  padding: 0px 10px;

  @media (max-width: 800px) {
    margin-bottom: 0px;
  }

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 5}px;
  }
`;

export default BrowseByStandardPage;
